import '/modules/common/css/ui/ui.css';
import '/modules/common/css/ui/ui.mobile.css';
import * as $ from "jquery";
import {Translator} from "../locale/Translator";
import {hash, isString, nn, nnTrim, prefixUrl} from "../util";
import isMobile from "is-mobile";
import {StatsGatherer} from "../StatsGatherer";
import {Tooltip} from "../ui/Tooltip";
import {MAX_SMALL_HEIGHT_THRESHOLD, MAX_SMALL_WIDTH_THRESHOLD} from "../config/config";
import {Base} from "../Base";
import {ContactButton} from "../ui/contact";
import {Modal2Thirds, ModalFullNoMargin, ModalHalf} from "../ui/Modal";
import {WarningsButton} from "../ui/warnings";
import {UIElement} from "../ui/ui";
import {formatTimestamp} from "../time/TimeCalculator";
import {IndicatorsSummary} from "../layers/IndicatorsSummary";
import {FetcherFactory} from "../fetch/Fetcher";

function initNamespace() {
    if (!window.tumbo)
        window.tumbo = {}
    tumbo.$ = $;
    window.$ = $;
}


/**
 * The main class of a Tumbo application
 * Should be extended to create a new Tumbo App
 * @abstract
 * @extends Base
 */
class MainTumboApplication extends Base {

    static ADMIN_HOTKEY = {ctrl: true, shift: true, alt: true, key: 'A'};
    static ADMIN_HASHED_SECRET = 'ecce9adfbc0aa59b3b7240954243c3ab7cc28cb7ae5420a453f1f85b881fd567';
    static ADMIN_UNLOCK_TIMEOUT = 1 * 60 * 60 * 1000; //1h

    static MAX_RUNTIMECONFIG_SESSION_TIME = 2 * 60 * 60 * 1000; //after 2 hours of "sleep" the application forgets its session and reloads as default

    /**
     *
     * @param translations {object} A dictionary of translation keys to translations
     * @param config {object} The application configuration to be parsed
     * @param targetId {string} The DOM element id to render this application in
     * @param logger {Logger}
     */
    constructor(translations, config, targetId, logger) {
        super();
        this.fetcherFactory = new FetcherFactory();
        tumbo.LOGGER = logger;
        tumbo.translator = new Translator(translations);
        tumbo.nnTrim = nnTrim;
        tumbo.nn = nn;

        this.config = config;
        this.targetId = targetId;
        if (targetId) {
            if (isString(targetId)) {
                this.target = $('#' + targetId);
                if (this.target.length === 0) {
                    throw 'No element found for targetId';
                }
            } else {
                throw 'Target must be the id of a DOM element';
            }
        } else {
            throw 'No target';
        }
    }

    getClassName() {
        return 'MainTumboApplication';
    }

    setFetcherFactory(fetcherFactory){
        this.fetcherFactory = fetcherFactory;
        return this;
    }

    /**
     * log stat entry for this application
     * @param action {string}
     * @param store {boolean} If true, stores immediately
     */
    logStat(action, store = false) {
        tumbo.STATS.logByCode(this.parsedConfig.getCode(), nnTrim(this.getClassName(), this.getClass()), action, store);
    }

    /**
     * Loads and validates the application configuration
     */
    loadConfig() {
        this.parsedConfig = this._readConfig();

        if (this.verifyErrors(true)) {
            if (this._getRuntimeConfigSerializationKey()) {
                try {
                    tumbo.code = this.parsedConfig.getCode();
                    const runtimeConfigSerialization = this._getRuntimeConfigSerialization();
                    tumbo.runtimeConfig = this._getRuntimeConfigFromSerialization(runtimeConfigSerialization);
                    this.parsedConfig.override(tumbo.runtimeConfig);
                    this.verifyErrorsAndReload();
                    tumbo.appCode = this.parsedConfig.getCode();
                } catch (e) {
                    tumbo.runtimeConfig = this._getRuntimeConfig();
                    this.logError("Could not read runtime config", e);
                }
            }
        } else {
            this.error = true;
        }
    }

    verifyErrorsAndReload() {
        if (!this.verifyErrors()) {
            this.parsedConfig.reset();
            tumbo.runtimeConfig.clean();
        }
    }

    _getRuntimeConfigSerialization() {
        if (this._isRuntimeConfigOutdated()) {
            this._clearRuntimeConfig();
        }
        return localStorage.getItem(this._getRuntimeConfigSerializationKey());
    }

    _clearRuntimeConfig() {
        localStorage.removeItem(this._getRuntimeConfigSerializationKey() + '_ts');
        localStorage.removeItem(this._getRuntimeConfigSerializationKey());
        this.logInfo("Clear localStorage config");
    }

    _isRuntimeConfigOutdated() {
        const tsStr = nnTrim(localStorage.getItem(this._getRuntimeConfigSerializationKey() + '_ts'));
        const ts = tsStr ? new Date(tsStr) : undefined;
        return ts && Math.abs(new Date() - ts) > MainTumboApplication.MAX_RUNTIMECONFIG_SESSION_TIME;
    }

    _setRuntimeConfigTS() {
        localStorage.setItem(this._getRuntimeConfigSerializationKey() + '_ts', formatTimestamp(new Date(), true));
    }

    _getRuntimeConfig() {
    }

    _getRuntimeConfigFromSerialization(runtimeConfigSerialization) {
    }

    _readConfig() {
    }

    _getRuntimeConfigSerializationKey() {
    }

    verifyErrors(write) {
        const errors = this.parsedConfig.verify();
        if (write && errors.length > 0 && tumbo.LOGGER.isOnDebug()) {
            const errStr = errors.join('<br/>');
            this.target.append($('<pre>' + errStr + '</pre>'));
        }
        errors.forEach(e => this.logError(e));
        return errors.length === 0;
    }

    /**
     * Runs this application. To be called from the outside.
     * Should not be overridden
     */
    run() {
        if (!this.error) {
            this.target.addClass('ccl-color_emergency ccl-style tumbo tumbo-target');

            tumbo.isMobileDevice = isMobile();
            if (tumbo.isMobileDevice) {
                this.target.addClass('mobile-device');
            } else {
                this.target.removeClass('mobile-device');
            }


            this.tooltip = new Tooltip(this.parsedConfig.getCode() + 'tooltip', this.target);

            this._initSizeMonitoring();

            this._doRun();

            tumbo.STATS = new StatsGatherer(this.parsedConfig.getCode(), this.parsedConfig.getStatsPostUrl(), this.target);
            tumbo.STATS.initSendProcedure();
            this.logStat("INIT");

            const contactPostUrl = this.parsedConfig.getContactPostUrl();
            if (contactPostUrl) {
                const modal = this.isSmallContainerWidth
                    ? new Modal2Thirds(this.target, 'contact_form_modal', tumbo.runtimeConfig, this.getOnChangeRuntimeConfigCallback(), true)
                    : new ModalHalf(this.target, 'contact_form_modal', tumbo.runtimeConfig, this.getOnChangeRuntimeConfigCallback(), true);
                this.contactButton = new ContactButton(modal, contactPostUrl, tumbo.runtimeConfig, this.getOnChangeRuntimeConfigCallback());
                this.target.append(this.contactButton.getUIElement());
            }

            this.isRunning = true;

            this.onSizeChange();

            $(document).on('keydown.adminUnlock', (e) => {
                if (!this._isAdminHotkey(e)) return;
                e.preventDefault();
                this._authAdmin(() => this.onAuthAdmin())
            });

            this._loadContextualHelp();
        }
        if (window.tumbo.tumboColours) {
            const elementById = document.getElementsByTagName('body')[0];
            for (let name in window.tumbo.tumboColours) {
                elementById.style.setProperty('--' + name, window.tumbo.tumboColours[name]);
            }
        }
    }

    _initWarnings() {
        this.warningsButton = new WarningsButton(
            this.parsedConfig.getGeneralWarningMessages(),
            this.parsedConfig.getAllServiceWarningMessages(),
            this.target,
            this.getOnChangeRuntimeConfigCallback()
        );
        this.warningsButton.onChangeVisibleLayers([]);
    }

    _enableContextualHelp(helpContents) {
        if (helpContents && Object.keys(helpContents).length > 0) {
            UIElement.helpContents = helpContents;
            const enableButton = $('<button>').addClass('enable-contextual-help');
            if (this._getRuntimeConfig().getSessionProp('contextualHelpEnabled', false)) {
                UIElement.enableContextualHelp();
                enableButton.addClass('enabled').attr('title', tumbo.translator.translate('help.disable'));
            } else enableButton.attr('title', tumbo.translator.translate('help.enable'))
            this.target.append(enableButton.click(() => {
                if (UIElement.contextualHelpEnabled) {
                    UIElement.disableContextualHelp();
                    enableButton.removeClass('enabled').attr('title', tumbo.translator.translate('help.enable'));
                    this._getRuntimeConfig().setSessionProp('contextualHelpEnabled', false)
                    this.logStat("DISABLE_CONTEXTUAL_HELP");
                } else {
                    UIElement.enableContextualHelp();
                    enableButton.addClass('enabled').attr('title', tumbo.translator.translate('help.disable'));
                    this._getRuntimeConfig().setSessionProp('contextualHelpEnabled', true)
                    this.logStat("ENABLE_CONTEXTUAL_HELP");
                }
            }));
        }
    }

    _loadContextualHelp() {
        const helpContentsUrl = this.parsedConfig.getHelpContentsUrl();
        if (helpContentsUrl) {
            this.fetcherFactory.getFetcher(helpContentsUrl)
                .success((data) => {
                    this.logDebug("Loaded " + Object.keys(data).length + " help content entries from endpoint");
                    this._enableContextualHelp(data);
                })
                .error((textStatus, error) => {
                    this.logError("Error on loading help contents [" + textStatus + "]", error);
                })
                .fetchJSON();
        } else {
            const helpContents = this.parsedConfig.getHelpContents()
            const helpCodes = helpContents ? Object.keys(helpContents) : [];
            if (helpCodes && helpCodes.length > 0) {
                this.logDebug("Loaded " + helpCodes.length + " help content entries from config")
                this._enableContextualHelp(helpContents);
            } else this.logDebug("No help contents to load from config");
        }
    }

    /**
     * Should be overridden to implement this application
     * @private
     */
    _doRun() {
    }

    /**
     * Should be overridden to make this application react to changes in window size
     */
    onSizeChange() {
        UIElement.allUIElements.forEach(e => {
            e.setContainerSizeAndPosition(this.isSmallContainerWidth, this.isSmallContainerHeight, this.containerWidth, this.containerHeight, this.containerAbsolutePosition);
        });
    }

    /**
     * Should not be overridden
     * @protected
     */
    _initSizeMonitoring() {
        this.target.append($(
            '<div class="landscape-error">' +
            '<div class="landscape-error-message">' + tumbo.translator.translate('landscapeErrorMessage') + '</div>' +
            '</div>'
        ));

        const onResize = () => {
            this.containerWidth = this.target.width();
            this.containerHeight = this.target.height();
            this.containerAbsolutePosition = this.target.offset();
            this.isSmallContainerWidth = this.containerWidth < MAX_SMALL_WIDTH_THRESHOLD
            this.isSmallContainerHeight = this.containerHeight < MAX_SMALL_HEIGHT_THRESHOLD;
            if (this.containerHeight < MAX_SMALL_HEIGHT_THRESHOLD / 2 && this.containerWidth > this.containerHeight * 1.5) {
                $('.landscape-error', this.target).show();
            } else $('.landscape-error', this.target).hide();

            if (this.isSmallContainerWidth) {
                this.target.addClass('small-tumbo-width').addClass('small-tumbo');
            } else {
                this.target.removeClass('small-tumbo-width');
            }
            if (this.isSmallContainerHeight) {
                this.target.addClass('small-tumbo-height').addClass('small-tumbo');
            } else {
                this.target.removeClass('small-tumbo-height');
            }
            if (!this.isSmallContainerHeight && !this.isSmallContainerWidth) {
                this.target.removeClass('small-tumbo');
            }

            if (this.isRunning)
                this.onSizeChange();
        }
        new ResizeObserver(onResize).observe(this.target[0]);
        onResize();
    }

    onChangeRuntimeConfig() {
        this.parsedConfig.override(tumbo.runtimeConfig);
    }

    getOnChangeRuntimeConfigCallback() {
        return () => this.onChangeRuntimeConfig()
    }

    initRuntimeConfigUpdate() {
        const key = this._getRuntimeConfigSerializationKey();
        if (key) {
            if (this.runtimeConfigIntervalId) {
                clearInterval(this.runtimeConfigIntervalId);
            }
            this.clearOnErrorCount = 0;
            this.runtimeConfigIntervalId = setInterval(() => {
                const serializedConfig = tumbo.runtimeConfig.getSerializedConfig();
                if (this._isRuntimeConfigOutdated()) {
                    this._clearRuntimeConfig();
                    if (tumbo.LOGGER.isOnDebug())
                        this.logDebug("Would refresh page");
                    else location.reload();
                }
                if (tumbo.LOGGER.isOnDebug() && new Date().getMinutes() % 5 === 0 && new Date().getSeconds() === 0) {
                    this.logDebug("Saving runtimeConfig with " + serializedConfig.length + " chars");
                    // this.logDebug("Saving runtimeConfig " + JSON.stringify(tumbo.runtimeConfig.getConfigOverrideObject()));
                }
                try {
                    this._setRuntimeConfigTS();
                    localStorage.setItem(key, serializedConfig);
                    this.clearOnErrorCount = 0;
                } catch (e) {
                    this.logError("Error saving runtime config to localStorage", e);
                    if (this.clearOnErrorCount < 3) {
                        localStorage.clear();
                        this.clearOnErrorCount++;
                        this.logInfo("Cleared localStorage");
                    } else {
                        clearInterval(this.runtimeConfigIntervalId);
                        this.logError('Already cleared localStorage ' + this.clearOnErrorCount + ' times. Will clear and reload.');
                        setTimeout(() => {
                            localStorage.clear();
                            location.reload();
                        }, 5000);
                    }
                }
            }, 1000);
        }
    }

    _isAdminHotkey(e) {
        const key = e.key.length === 1 ? e.key.toUpperCase() : e.key;
        return (!!e.ctrlKey === !!MainTumboApplication.ADMIN_HOTKEY.ctrl)
            && (!!e.shiftKey === !!MainTumboApplication.ADMIN_HOTKEY.shift)
            // && (!!e.altKey === !!MainTumboApplication.ADMIN_HOTKEY.alt)
            && (key === MainTumboApplication.ADMIN_HOTKEY.key.toUpperCase());
    }

    _authAdmin(onAuth) {
        if (this.isAdminUnlocked()) {
            this._unlockAdmin();
            onAuth();
            return;
        }
        const secret = prompt('Admin secret:');
        this._validateAdminSecret(secret,
            () => {
                this._unlockAdmin();
                onAuth();
            },
            () => {
                this._lockAdmin();
                setTimeout(() => alert("Wrong secret"), 500);
            }
        );
    }

    //Redefine in subclasses to define what happens when admin authenticates
    onAuthAdmin() {
        tumbo.LOGGER.setDebug(true)
    }

    _validateAdminSecret(secret, onValid, onNotValid) {
        const h = hash(secret).then((res) => {
            // if (res === MainTumboApplication.ADMIN_HASHED_SECRET)
            if (tumbo.LOGGER.onDebug || res === MainTumboApplication.ADMIN_HASHED_SECRET)
                onValid();
            else onNotValid();
        });
        return h === MainTumboApplication.ADMIN_HASHED_SECRET;
    }

    isAdminUnlocked() {
        const unlockTime = this.get("admin_unlocked");
        return unlockTime && new Date().getTime() - unlockTime < MainTumboApplication.ADMIN_UNLOCK_TIMEOUT;
    }

    _unlockAdmin() {
        return this.set("admin_unlocked", new Date().getTime());
    }

    _lockAdmin() {
        return this.set("admin_unlocked", false);
    }

}

class IndicatorBasedTumboApplication extends MainTumboApplication {

    /**
     *
     * @param translations {object} A dictionary of translation keys to translations
     * @param config {object} The application configuration to be parsed
     * @param productSettingsUrl  {string|undefined} A URL to get settings for products by product code (disabled, provisional, deficient, etc.)
     * @param targetId {string} The DOM element id to render this application in
     * @param logger {Logger}
     */
    constructor(translations, config, productSettingsUrl, targetId, logger) {
        super(translations, config, targetId, logger);
        this.productSettingsUrl = nnTrim(productSettingsUrl);
    }

    getClassName() {
        return 'IndicatorBasedTumboApplication';
    }

    async loadAndRun() {
        try {
            await this.loadConfig();
        } catch (e) {
        }
        this.run();
    }

    loadConfig() {
        if (this.productSettingsUrl)
            return this.fetcherFactory.getFetcher(prefixUrl(this.productSettingsUrl))
                .success(data => this.productSettings = data)
                .error((textStatus, error) => this.logError("Error getting product settings: " + textStatus, error))
                .always(() => super.loadConfig())
                .fetchJSONPromise();
        else return new Promise((resolve, reject) => {
            super.loadConfig();
            resolve();
        });
    }

    verifyErrorsAndReload() {
        if (!this.verifyErrors()) {
            this.parsedConfig.reset(this.productSettings);
            tumbo.runtimeConfig.clean();
        }
    }

    setIndicators(indicators) {
        this.indicators = indicators;
        const prodCodes = new Set();
        this.allIndicators = [];
        this.indicators.forEach(i => {
            const code = (i.hasTimeScale() ? i.getProductConfigForTimeScale(i.getTimeScales()[0]) : i.getProductConfig())?.getCode();
            if (code && !prodCodes.has(code)) {
                prodCodes.add(code);
                this.allIndicators.push(i);
            }
        });
        if (!this.modalAdmin)
            this.modalAdmin = new ModalFullNoMargin(this.target, 'modal_admin');
    }

    onAuthAdmin() {
        super.onAuthAdmin();
        this.showAdminModal();
    }

    showAdminModal() {
        this.modalAdmin
            .setContent(this.getAdminModalContent())
            .setTitle("Admin")
            .show();
    }

    getAdminModalContent() {
        return new IndicatorsSummary('indicators_summary', this.indicators).getUIElement();
    }
}

/**
 * Set a colour by its name. It will be used in the map style.
 * Possible colour names:
 *     . fundamental
 *     . fundamental-half-opacity
 *     . fundamental-contrast
 *     . background-disabled
 *     . text-background-disabled
 *     . border-disabled
 *     . background-table-header
 *     . text
 *     . text-disabled
 *     . text-disabled-light
 *     . text-context
 *
 * Call to this function should be made before calling the init function
 *
 * @param colourName {string}
 * @param colour {string} Any CSS colour
 */
function setColour(colourName, colour) {
    if (!window.tumbo.tumboColours)
        window.tumbo.tumboColours = {};
    window.tumbo.tumboColours[colourName] = colour
}

initNamespace();
window.tumbo.setColour = setColour;

export {MainTumboApplication, IndicatorBasedTumboApplication, initNamespace, setColour}